import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Typography } from "antd";
import { AxiosError } from "axios";
import { bindCompanies } from "entities/companies";
import { getProfile } from "entities/viewer";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "shared/lib/antd-global";
import ROUTES from "shared/routes";
import { Input } from "shared/ui/Input";
import { confirmCode, login } from "../api";

export const CodeForm = ({
  phone,
  token,
}: {
  phone: string;
  token: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const ref = localStorage.getItem("bind-companies-ref");
  const refFromParams = searchParams.get("ref");
  const [code, setCode] = useState<string>("");
  const [timer, setTimer] = useState<number>(60);
  const [isTimerActive, setIsTimerActive] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [isProfileEnabled, setIsProfileEnabled] = useState<boolean>(false);

  const { mutate: bindCompaniesMutate } = useMutation({
    mutationFn: bindCompanies,
    onSuccess() {
      message.success("Update profile success");
      localStorage.removeItem("bind-companies-ref");
    },
    onError(error: AxiosError<{ message: string }>) {
      message.error(error.response?.data?.message || "Update profile error");
      localStorage.removeItem("bind-companies-ref");
    },
    retry: 2,
    retryDelay: 1000,
  });

  const { mutate: confirmCodeMutate, isPending: isConfirmCodeLoading } =
    useMutation({
      mutationFn: (params: { phone: string; code: string; token: string }) =>
        confirmCode(params),
      onSuccess(response) {
        if (response.data.accessToken) {
          localStorage.setItem("token", response.data?.accessToken);

          if (ref || refFromParams) {
            bindCompaniesMutate(ref || refFromParams!);
          }

          setIsProfileEnabled(true);
        } else {
          setError(t("auth.get_code_error"));
        }
      },
      onError(error: any) {
        setError(error?.response?.data?.message || error?.message || t("auth.get_code_error"));
      },
    });

  const { mutate: loginMutate } = useMutation({
    mutationFn: (params: { phone: string }) => login(params),
    onSuccess(response) {
      navigate(ROUTES.CODE, {
        state: { phone, token: response.data?.token },
      });
    },
    onError(error: any) {
      if (error.status === 404) {
        setError(t("auth.phone_number_undefined"));
      } else {
        setError(error?.response?.data?.message || error?.message || t("auth.phone_number_error"));
      }
    },
  });

  const { data: profile, isLoading: isProfileLoading } = useQuery({
    queryKey: ["profile"],
    queryFn: () => getProfile(),
    enabled: isProfileEnabled,
    gcTime: 0,
    staleTime: 0,
  });

  useEffect(() => {
    if (!isProfileEnabled || !profile) return;

    if (
      profile.identity_verification_provider &&
      profile.need_identity_verification &&
      profile.identity_verification_blocked
    ) {
      navigate(ROUTES.IDENTITY_VERIFICATION);
    } else if (profile.need_register) {
      navigate(ROUTES.REGISTRATION);
    } else if (profile.need_sign_personal_data) {
      navigate(ROUTES.SIGN_PERSONAL_DATA);
    } else {
      const pathname = localStorage.getItem("pathname");
      if (pathname) {
        navigate(pathname);
      } else {
        navigate(ROUTES.MAIN);
      }
    }
  }, [profile, isProfileEnabled, navigate]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isTimerActive && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsTimerActive(false);
    }

    return () => clearInterval(interval);
  }, [isTimerActive, timer]);

  const handleResendCode = () => {
    setTimer(60);
    setIsTimerActive(true);
    sentPhoneAgain();
  };

  const sentCode = () => {
    confirmCodeMutate({
      phone: phone.replace(/\D/g, ""),
      code: code.replace(/\D/g, ""),
      token,
    });
  };

  const sentPhoneAgain = () => {
    loginMutate({ phone: phone.replace(/\D/g, "") });
  };

  return (
    <div>
      <Typography.Title level={3}>{t("auth.title")}</Typography.Title>

      <div>
        <p>{t("code.enter_code")}</p>

        <Input
          mask="999999"
          name="additional-code"
          type="tel"
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={{ textAlign: "center" }}
        />

        <p>
          {t("code.code_sent")} {phone}
        </p>

        {!!error && (
          <Typography.Text
            style={{ marginBottom: 16, display: "block" }}
            type="danger"
          >
            {error}
          </Typography.Text>
        )}

        <Button
          block
          type="primary"
          disabled={code.replace(/\D/g, "").length < 6}
          onClick={sentCode}
          style={{ marginBottom: 16 }}
          loading={isConfirmCodeLoading || isProfileLoading}
        >
          {t("code.confirm")}
        </Button>

        <Button
          block
          type="text"
          onClick={handleResendCode}
          disabled={isTimerActive}
          style={{ marginBottom: 16 }}
        >
          {isTimerActive
            ? `${t("code.resend_timer")} ${timer} ${t("code.seconds")}`
            : t("code.resend_code")}
        </Button>

        <Button
          block
          type="text"
          onClick={() => navigate(`${ROUTES.LOGIN}?${searchParams.toString()}`)}
        >
          {t("code.change_number")}
        </Button>
      </div>
    </div>
  );
};
